import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { FiHelpCircle, FiUsers, FiBarChart2, FiTrendingUp, FiShield, FiFileText, FiArrowRight, FiCheck, FiClock, FiDownload } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import Toggle from '../Toggle';
import { toast } from '../Toast';
import { DownloadReportModal } from './ReportModals';
import { REPORTS, SAMPLE_MADE, ReportKey, useRegistryData, asAtDate } from './reportsData';

const ICONS: Record<string, React.ReactNode> = {
    'top-20': <FiUsers />, 'holders-by-size': <FiBarChart2 />, 'who-bought-and-sold': <FiTrendingUp />, 'substantial-holders': <FiShield />,
};

// Share Registry › Reports (Figma 208:3978, note 528:1857): four ready-made reports, the monthly board pack and past reports.
// Make This Report / Download open the Download modal (550:2576); its PDF option opens the printable report (ReportView.tsx).
const Reports: React.FC = () => {
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const data = useRegistryData(stock);
    const [download, setDownload] = useState<ReportKey | null>(null);
    // monthly email: no setting or API behind it yet, so the switch is local and preview only
    const [monthly, setMonthly] = useState<Record<string, boolean>>({});
    const asAt = asAtDate(data.listing, 'D MMM YYYY');
    const month = moment().format('MMMM');

    const status = data.loading ? 'Checking the register…' : asAt ? `Register as at ${asAt}` : 'Register date —';

    const toggleMonthly = (key: ReportKey, on: boolean) => {
        setMonthly({ ...monthly, [key]: on });
        toast.info('Preview only: the monthly report email isn’t connected yet.');
    };

    return <div className="registry-reports">
        <div className="page-header">
            <div>
                <h1>Reports</h1>
                <p>Ready‑made reports for your board pack, your annual report and the ASX. Pick one and Investi makes it.</p>
            </div>
        </div>

        <div className="reports-banner">
            <span className="icon-tile"><FiHelpCircle /></span>
            <div>
                <strong>What are these for?</strong>
                <p>A few registry reports are needed every year — the Top 20 and the holder bands go in your annual report, and the board usually wants to see who bought and sold each month. Instead of exporting spreadsheets, press one button here.</p>
            </div>
        </div>

        {data.error && <p className="error">{data.error}</p>}

        <div className="reports-grid" data-tour="reports-cards">
            {REPORTS.map(r => <section key={r.key} className="card reports-card">
                <div className="reports-card-head">
                    <span className="icon-tile">{ICONS[r.key]}</span>
                    <div><h2>{r.title}</h2><span className="reports-muted">{status}</span></div>
                </div>
                <p className="reports-what">{r.what}</p>
                <div className="reports-why"><FiHelpCircle /> <span>{r.why}</span></div>
                <button type="button" className="btn-primary reports-make" onClick={() => setDownload(r.key)}><FiFileText /> Make This Report</button>
                <div className="reports-setting">
                    <div>
                        <strong>Email it to me every month</strong>
                        <span>{monthly[r.key] ? 'On · 1st of the month (preview)' : 'Off'}</span>
                    </div>
                    <Toggle checked={!!monthly[r.key]} onChange={on => toggleMonthly(r.key, on)} label={`Email ${r.title} every month`} />
                </div>
            </section>)}
        </div>

        <section className="reports-pack" data-tour="reports-pack">
            <div className="reports-pack-head">
                <span className="icon-tile dark"><FiFileText /></span>
                <div>
                    <h2>The monthly board pack</h2>
                    <p>All four reports above in one PDF, with a short written summary at the front. This is what most directors actually read.</p>
                </div>
                <button type="button" className="btn-primary" onClick={() => setDownload('board-pack')}><FiArrowRight /> Make the {month} Pack</button>
            </div>
            <ul className="reports-pack-includes">
                {['Top 20 shareholders', 'Holders by size', 'Who bought and sold', 'Substantial holders', 'Announcement and email results'].map(x =>
                    <li key={x}><FiCheck /> {x}</li>)}
            </ul>
        </section>

        <section className="card reports-made">
            <div className="reports-card-head">
                <span className="icon-tile"><FiClock /></span>
                <div><h2>Reports You’ve Made</h2><span className="reports-sub">Kept for 12 months · sample rows until report history is connected</span></div>
            </div>
            {/* sample-labelled preview rows: there is no report history API yet (see SAMPLE_MADE in reportsData.ts) */}
            {SAMPLE_MADE.map(m => <div key={m.key} className="reports-made-row">
                <span className="reports-file-tile"><FiFileText /></span>
                <div className="reports-made-t">
                    <strong>{m.title} <span className="pill warn">Sample</span></strong>
                    <span>— · {m.sub}</span>
                </div>
                <span className="reports-muted">{m.file} · —</span>
                <button type="button" className="btn-outline" onClick={() => setDownload(m.key)}><FiDownload /> Download</button>
            </div>)}
        </section>

        {download && stock && <DownloadReportModal stock={stock} data={data} initial={download} onClose={() => setDownload(null)} />}
    </div>;
};

export default Reports;
